/**
 * Translations for the shared <EditCustomerModal /> component.
 *
 * Used by both the dealer portal (where the active language comes
 * from `dealer_lang`) and the admin/IT customers list (where it comes
 * from `admin_lang`). Falls back to IT when no key is selected.
 *
 * Strings are scoped narrowly to this modal so we don't duplicate the
 * giant `dealerPortal.js` catalogue. New strings should go here when
 * they're added to the modal.
 *
 * Fabrizio CR 01/03/2026 — Pierre Cardin (dealer FR) reported every
 * label and banner of this modal was rendered in Italian even though
 * the rest of his dealer UI was in French. Root cause: the modal was
 * created before the i18n bus existed and was never retrofitted.
 */

const BASE = {
  // Header
  title: "Modifica Cliente",
  badge_private: "Privato",
  badge_business: "Business",
  badge_private_tt: "Cliente Privato (B2C)",
  badge_business_tt: "Cliente Business (B2B)",

  // Locked banners
  contract_locked_title: "Contratto firmato — Modifica bloccata",
  contract_locked_body:
    "I dati di questo cliente sono presenti nel contratto firmato digitalmente e non possono essere modificati per motivi legali e GDPR. Per correzioni, contattare l'amministratore di sistema.",

  // Admin override
  override_active_title: "⚠️  Override admin ATTIVO — modifiche tracciate in audit log",
  override_available_title: "Override admin disponibile",
  override_active_body:
    "Stai per modificare un cliente bloccato ({reason}). Ogni campo cambiato verrà registrato con valore prima/dopo, il tuo identificativo ({email}) e l'IP di provenienza nella collection customer_admin_override_logs.",
  override_disabled_body:
    "Sei loggato come {role}. Per motivi di sicurezza, le modifiche su clienti bloccati sono disattivate di default. Attiva l'override solo se necessario (GDPR Art. 16, correzioni di typo, support cliente). L'azione verrà tracciata in audit log.",
  override_reason_signed: "contratto firmato",
  override_reason_compiling: "auto-compilazione in corso",
  override_enable: "Abilita override",
  override_disable: "Disattiva override",

  // Form labels
  first_name: "Nome",
  last_name: "Cognome",
  email: "Email",
  phone: "Telefono",
  country: "Paese",
  service: "Servizio",
  vehicle_brand: "Marca Veicolo",
  vehicle_model: "Modello Veicolo",
  vin: "VIN",
  plate: "Targa",

  // Buttons
  close: "Chiudi",
  cancel: "Annulla",
  save: "Salva Modifiche",
  save_override: "Salva con override",
  saving: "Salvataggio...",

  // Footnote
  unlocked_note:
    "Modifiche permesse perché il contratto NON è ancora stato firmato. Dopo la firma, i dati saranno congelati per ragioni legali.",
  override_note:
    "Override admin attivo — ogni modifica salvata genererà un audit log dedicato con diff prima/dopo.",

  // Toasts
  saved: "Cliente aggiornato con successo",
  save_error: "Errore aggiornamento cliente",
  edit_not_allowed: "Modifica non consentita",
  vin_dupe_owner: "altro cliente",
  vin_dupe_msg:
    "VIN {vin} già registrato nel sistema (cliente: {owner}). Verifica il numero e riprova.",

  // Missing-field asterisk tooltip (Zorzutti CR 05/08/2026)
  //
  // The V2 contract requires 10+ mandatory fields; when any of them
  // is blank the diagnostics widget flags the label with a red *.
  // The tooltip was hardcoded in Italian and leaked into every
  // non-IT dealer portal — now goes through the i18n bus.
  missing_v2_tooltip: "Campo obbligatorio per il contratto V2 — attualmente mancante",

  // Last-email widget (Zorzutti CR 31/07/2026)
  lastEmailTitle: "Ultima email inviata",
  lastEmailLoading: "Caricamento ultima email…",
  lastEmailEmpty: "Nessuna email ancora inviata a questo cliente",
  lastEmailFetchError: "Impossibile caricare l'ultima email",
  lastEmailType: "Tipo",
  lastEmailSentAt: "Inviata",
  lastEmailOpenedAt: "Aperta",
  lastEmailClickedAt: "Firma / attivazione",
  lastEmailStatusSent: "Inviata",
  lastEmailStatusOpened: "Aperta",
  lastEmailStatusClicked: "Cliente attivo",
  lastEmailStatusFailed: "Errore invio",
  lastEmailMockedShort: "stima",
  lastEmailMockedHint: "Stima basata sull'attività wallet — il tracking reale arriverà con la configurazione AWS SES",
  lastEmailMockedFooter: "Il tracking di aperture e click sarà attivato non appena AWS SES Event Publishing verrà configurato sull'account LoJack.",
  // History timeline (Zorzutti CR 01/08/2026)
  lastEmailHistoryTitle: "Cronologia recente",
  lastEmailHistoryLoading: "Caricamento cronologia…",
  lastEmailHistoryEmpty: "Nessuna email precedente",
  lastEmailHistoryError: "Impossibile caricare la cronologia",
  timeJustNow: "adesso",
  timeMinutesAgo: "{n} min fa",
  timeHoursAgo: "{n} h fa",
  timeDaysAgo: "{n} g fa",
  // Human-readable labels for common email_type values (used by the widget)
  emailType_welcome: "Benvenuto",
  emailType_sollecito: "Sollecito",
  emailType_contract_signed: "Contratto firmato",
  emailType_otp_signature: "OTP firma",
  emailType_self_update_otp: "OTP auto-aggiornamento",
  emailType_self_update_confirmation: "Conferma auto-aggiornamento",
  emailType_customer_change_request_resolution: "Esito richiesta modifica",
  emailType_wallet_correction_resend: "Wallet re-inviato",
  emailType_gdpr_erasure: "Ricevuta cancellazione GDPR",
  emailType_test_send: "Email di test",
};

const TRANSLATIONS = {
  IT: BASE,
  FR: {
    ...BASE,
    title: "Modifier le client",
    badge_private: "Privé",
    badge_business: "Entreprise",
    badge_private_tt: "Client Privé (B2C)",
    badge_business_tt: "Client Entreprise (B2B)",
    contract_locked_title: "Contrat signé — Modification bloquée",
    contract_locked_body:
      "Les données de ce client figurent dans le contrat signé numériquement et ne peuvent être modifiées pour des raisons légales et RGPD. Pour toute correction, contactez l'administrateur système.",
    override_active_title: "⚠️  Override admin ACTIF — modifications enregistrées dans le journal d'audit",
    override_available_title: "Override admin disponible",
    override_active_body:
      "Vous êtes sur le point de modifier un client verrouillé ({reason}). Chaque champ modifié sera enregistré avec valeur avant/après, votre identifiant ({email}) et l'IP source dans la collection customer_admin_override_logs.",
    override_disabled_body:
      "Vous êtes connecté en tant que {role}. Pour des raisons de sécurité, les modifications sur les clients verrouillés sont désactivées par défaut. Activez l'override uniquement si nécessaire (RGPD Art. 16, correction de typo, support client). L'action sera tracée dans le journal d'audit.",
    override_reason_signed: "contrat signé",
    override_reason_compiling: "auto-saisie en cours",
    override_enable: "Activer override",
    override_disable: "Désactiver override",
    first_name: "Prénom",
    last_name: "Nom",
    email: "E-mail",
    phone: "Téléphone",
    country: "Pays",
    service: "Service",
    vehicle_brand: "Marque du véhicule",
    vehicle_model: "Modèle du véhicule",
    vin: "VIN",
    plate: "Plaque d'immatriculation",
    close: "Fermer",
    cancel: "Annuler",
    save: "Enregistrer",
    save_override: "Enregistrer avec override",
    saving: "Enregistrement...",
    unlocked_note:
      "Modifications autorisées car le contrat n'a PAS encore été signé. Après la signature, les données seront figées pour des raisons légales.",
    override_note:
      "Override admin actif — chaque modification enregistrée générera un journal d'audit dédié avec diff avant/après.",
    saved: "Client mis à jour avec succès",
    save_error: "Erreur de mise à jour du client",
    edit_not_allowed: "Modification non autorisée",
    vin_dupe_owner: "autre client",
    vin_dupe_msg:
      "VIN {vin} déjà enregistré dans le système (client : {owner}). Vérifiez le numéro et réessayez.",
    missing_v2_tooltip: "Champ obligatoire pour le contrat V2 — actuellement manquant",
    // Last-email widget
    lastEmailTitle: "Dernier e-mail envoyé",
    lastEmailLoading: "Chargement du dernier e-mail…",
    lastEmailEmpty: "Aucun e-mail envoyé à ce client pour le moment",
    lastEmailFetchError: "Impossible de charger le dernier e-mail",
    lastEmailType: "Type", lastEmailSentAt: "Envoyé",
    lastEmailOpenedAt: "Ouvert", lastEmailClickedAt: "Signé / activé",
    lastEmailStatusSent: "Envoyé", lastEmailStatusOpened: "Ouvert",
    lastEmailStatusClicked: "Client actif", lastEmailStatusFailed: "Échec envoi",
    lastEmailMockedShort: "est.",
    lastEmailMockedHint: "Estimation basée sur l'activité wallet — le suivi réel arrivera avec la configuration AWS SES",
    lastEmailMockedFooter: "Le suivi des ouvertures et des clics sera activé une fois AWS SES event publishing configuré sur le compte LoJack.",
    lastEmailHistoryTitle: "Historique récent",
    lastEmailHistoryLoading: "Chargement de l'historique…",
    lastEmailHistoryEmpty: "Aucun e-mail précédent",
    lastEmailHistoryError: "Impossible de charger l'historique",
    timeJustNow: "à l'instant", timeMinutesAgo: "il y a {n} min",
    timeHoursAgo: "il y a {n} h", timeDaysAgo: "il y a {n} j",
    emailType_welcome: "Bienvenue", emailType_sollecito: "Relance",
    emailType_contract_signed: "Contrat signé", emailType_otp_signature: "OTP signature",
    emailType_self_update_otp: "OTP auto-mise à jour",
    emailType_self_update_confirmation: "Confirmation auto-mise à jour",
    emailType_customer_change_request_resolution: "Résultat demande de modif.",
    emailType_wallet_correction_resend: "Wallet renvoyé",
    emailType_gdpr_erasure: "Reçu effacement RGPD", emailType_test_send: "E-mail de test",
  },
  EN: {
    ...BASE,
    title: "Edit customer",
    badge_private: "Private", badge_business: "Business",
    badge_private_tt: "Private customer (B2C)", badge_business_tt: "Business customer (B2B)",
    contract_locked_title: "Contract signed — Editing blocked",
    contract_locked_body:
      "This customer's data is part of the digitally signed contract and cannot be modified for legal and GDPR reasons. For corrections, contact the system administrator.",
    override_active_title: "⚠️  Admin override ACTIVE — changes tracked in audit log",
    override_available_title: "Admin override available",
    override_active_body:
      "You're about to edit a locked customer ({reason}). Every modified field will be recorded with before/after value, your identifier ({email}) and source IP in the customer_admin_override_logs collection.",
    override_disabled_body:
      "You're logged in as {role}. For security reasons, edits to locked customers are disabled by default. Enable the override only when necessary (GDPR Art. 16, typo fixes, customer support). The action will be tracked in the audit log.",
    override_reason_signed: "contract signed",
    override_reason_compiling: "self-compilation in progress",
    override_enable: "Enable override", override_disable: "Disable override",
    first_name: "First name", last_name: "Last name", email: "Email",
    phone: "Phone", country: "Country", service: "Service",
    vehicle_brand: "Vehicle brand", vehicle_model: "Vehicle model",
    vin: "VIN", plate: "Number plate",
    close: "Close", cancel: "Cancel",
    save: "Save changes", save_override: "Save with override",
    saving: "Saving...",
    unlocked_note: "Edits allowed because the contract has NOT been signed yet. After signing, the data will be frozen for legal reasons.",
    override_note: "Admin override active — every saved change generates a dedicated audit log with before/after diff.",
    saved: "Customer updated successfully", save_error: "Customer update error",
    edit_not_allowed: "Edit not allowed",
    vin_dupe_owner: "another customer",
    vin_dupe_msg: "VIN {vin} is already registered in the system (customer: {owner}). Check the number and try again.",
    missing_v2_tooltip: "Mandatory field for the V2 contract — currently missing",
    // Last-email widget
    lastEmailTitle: "Last email sent",
    lastEmailLoading: "Loading last email…",
    lastEmailEmpty: "No email has been sent to this customer yet",
    lastEmailFetchError: "Unable to load the last email",
    lastEmailType: "Type", lastEmailSentAt: "Sent",
    lastEmailOpenedAt: "Opened", lastEmailClickedAt: "Signed / activated",
    lastEmailStatusSent: "Sent", lastEmailStatusOpened: "Opened",
    lastEmailStatusClicked: "Customer active", lastEmailStatusFailed: "Send failed",
    lastEmailMockedShort: "est.",
    lastEmailMockedHint: "Estimate based on wallet activity — real tracking arrives with AWS SES setup",
    lastEmailMockedFooter: "Open & click tracking will be enabled once AWS SES event publishing is configured on the LoJack account.",
    lastEmailHistoryTitle: "Recent history",
    lastEmailHistoryLoading: "Loading history…",
    lastEmailHistoryEmpty: "No prior emails on record",
    lastEmailHistoryError: "Unable to load history",
    timeJustNow: "just now", timeMinutesAgo: "{n} min ago",
    timeHoursAgo: "{n} h ago", timeDaysAgo: "{n} d ago",
    emailType_welcome: "Welcome", emailType_sollecito: "Reminder",
    emailType_contract_signed: "Signed contract", emailType_otp_signature: "OTP signature",
    emailType_self_update_otp: "Self-update OTP",
    emailType_self_update_confirmation: "Self-update confirmation",
    emailType_customer_change_request_resolution: "Change request outcome",
    emailType_wallet_correction_resend: "Wallet re-sent",
    emailType_gdpr_erasure: "GDPR erasure receipt", emailType_test_send: "Test email",
  },
  ES: {
    ...BASE,
    title: "Modificar cliente",
    badge_private: "Privado", badge_business: "Empresa",
    badge_private_tt: "Cliente Privado (B2C)", badge_business_tt: "Cliente Empresa (B2B)",
    contract_locked_title: "Contrato firmado — Edición bloqueada",
    contract_locked_body: "Los datos de este cliente figuran en el contrato firmado digitalmente y no pueden modificarse por motivos legales y RGPD. Para correcciones, contacte al administrador del sistema.",
    override_active_title: "⚠️  Override admin ACTIVO — cambios registrados en el log de auditoría",
    override_available_title: "Override admin disponible",
    override_active_body: "Está a punto de modificar un cliente bloqueado ({reason}). Cada campo modificado se registrará con valor antes/después, su identificador ({email}) y la IP de origen en la colección customer_admin_override_logs.",
    override_disabled_body: "Ha iniciado sesión como {role}. Por motivos de seguridad, las modificaciones a clientes bloqueados están deshabilitadas por defecto. Active el override solo si es necesario (RGPD Art. 16, corrección de erratas, soporte al cliente). La acción se registrará en el log de auditoría.",
    override_reason_signed: "contrato firmado", override_reason_compiling: "autocompilación en curso",
    override_enable: "Activar override", override_disable: "Desactivar override",
    first_name: "Nombre", last_name: "Apellido", email: "Email", phone: "Teléfono",
    country: "País", service: "Servicio",
    vehicle_brand: "Marca del vehículo", vehicle_model: "Modelo del vehículo",
    vin: "VIN", plate: "Matrícula",
    close: "Cerrar", cancel: "Cancelar",
    save: "Guardar cambios", save_override: "Guardar con override", saving: "Guardando...",
    unlocked_note: "Modificaciones permitidas porque el contrato AÚN no ha sido firmado. Después de la firma, los datos quedarán congelados por motivos legales.",
    override_note: "Override admin activo — cada cambio guardado generará un log de auditoría dedicado con diff antes/después.",
    saved: "Cliente actualizado correctamente", save_error: "Error al actualizar cliente",
    edit_not_allowed: "Modificación no permitida",
    vin_dupe_owner: "otro cliente",
    vin_dupe_msg: "VIN {vin} ya registrado en el sistema (cliente: {owner}). Verifique el número y reinténtelo.",
    missing_v2_tooltip: "Campo obligatorio para el contrato V2 — actualmente ausente",
    // Last-email widget
    lastEmailTitle: "Último email enviado",
    lastEmailLoading: "Cargando último email…",
    lastEmailEmpty: "Ningún email enviado aún a este cliente",
    lastEmailFetchError: "No se puede cargar el último email",
    lastEmailType: "Tipo", lastEmailSentAt: "Enviado",
    lastEmailOpenedAt: "Abierto", lastEmailClickedAt: "Firmado / activado",
    lastEmailStatusSent: "Enviado", lastEmailStatusOpened: "Abierto",
    lastEmailStatusClicked: "Cliente activo", lastEmailStatusFailed: "Error de envío",
    lastEmailMockedShort: "est.",
    lastEmailMockedHint: "Estimación basada en la actividad del wallet — el seguimiento real llegará con la configuración de AWS SES",
    lastEmailMockedFooter: "El seguimiento de aperturas y clics se activará una vez configurado AWS SES event publishing en la cuenta LoJack.",
    lastEmailHistoryTitle: "Historial reciente",
    lastEmailHistoryLoading: "Cargando historial…",
    lastEmailHistoryEmpty: "No hay emails previos",
    lastEmailHistoryError: "No se puede cargar el historial",
    timeJustNow: "ahora", timeMinutesAgo: "hace {n} min",
    timeHoursAgo: "hace {n} h", timeDaysAgo: "hace {n} d",
    emailType_welcome: "Bienvenida", emailType_sollecito: "Recordatorio",
    emailType_contract_signed: "Contrato firmado", emailType_otp_signature: "OTP firma",
    emailType_self_update_otp: "OTP autoactualización",
    emailType_self_update_confirmation: "Confirmación autoactualización",
    emailType_customer_change_request_resolution: "Resultado solicitud de cambio",
    emailType_wallet_correction_resend: "Wallet reenviado",
    emailType_gdpr_erasure: "Comprobante borrado RGPD", emailType_test_send: "Email de prueba",
  },
  DE: {
    ...BASE,
    title: "Kunden bearbeiten",
    badge_private: "Privat", badge_business: "Geschäftlich",
    badge_private_tt: "Privatkunde (B2C)", badge_business_tt: "Geschäftskunde (B2B)",
    contract_locked_title: "Vertrag unterzeichnet — Bearbeitung gesperrt",
    contract_locked_body: "Die Daten dieses Kunden sind Teil des digital unterzeichneten Vertrags und können aus rechtlichen und DSGVO-Gründen nicht geändert werden. Für Korrekturen wenden Sie sich an den Systemadministrator.",
    override_active_title: "⚠️  Admin-Override AKTIV — Änderungen im Audit-Log protokolliert",
    override_available_title: "Admin-Override verfügbar",
    override_active_body: "Sie sind dabei, einen gesperrten Kunden zu bearbeiten ({reason}). Jedes geänderte Feld wird mit Vorher/Nachher-Wert, Ihrer Kennung ({email}) und der Quell-IP in der customer_admin_override_logs-Collection erfasst.",
    override_disabled_body: "Sie sind als {role} angemeldet. Aus Sicherheitsgründen sind Änderungen an gesperrten Kunden standardmäßig deaktiviert. Aktivieren Sie das Override nur wenn nötig (DSGVO Art. 16, Tippfehlerkorrektur, Kundensupport). Die Aktion wird im Audit-Log protokolliert.",
    override_reason_signed: "Vertrag unterzeichnet", override_reason_compiling: "Selbsteingabe läuft",
    override_enable: "Override aktivieren", override_disable: "Override deaktivieren",
    first_name: "Vorname", last_name: "Nachname", email: "E-Mail", phone: "Telefon",
    country: "Land", service: "Service",
    vehicle_brand: "Fahrzeugmarke", vehicle_model: "Fahrzeugmodell",
    vin: "VIN", plate: "Kennzeichen",
    close: "Schließen", cancel: "Abbrechen",
    save: "Änderungen speichern", save_override: "Mit Override speichern", saving: "Speichern...",
    unlocked_note: "Änderungen erlaubt, da der Vertrag NOCH NICHT unterzeichnet wurde. Nach der Unterzeichnung werden die Daten aus rechtlichen Gründen eingefroren.",
    override_note: "Admin-Override aktiv — jede gespeicherte Änderung erzeugt ein dediziertes Audit-Log mit Vorher/Nachher-Diff.",
    saved: "Kunde erfolgreich aktualisiert", save_error: "Fehler beim Aktualisieren des Kunden",
    edit_not_allowed: "Bearbeitung nicht zulässig",
    vin_dupe_owner: "anderer Kunde",
    vin_dupe_msg: "VIN {vin} ist bereits im System registriert (Kunde: {owner}). Überprüfen Sie die Nummer und versuchen Sie es erneut.",
    missing_v2_tooltip: "Pflichtfeld für den V2-Vertrag — derzeit fehlend",
    // Last-email widget
    lastEmailTitle: "Letzte gesendete E-Mail",
    lastEmailLoading: "Letzte E-Mail wird geladen…",
    lastEmailEmpty: "Bisher wurde keine E-Mail an diesen Kunden gesendet",
    lastEmailFetchError: "Letzte E-Mail konnte nicht geladen werden",
    lastEmailType: "Typ", lastEmailSentAt: "Gesendet",
    lastEmailOpenedAt: "Geöffnet", lastEmailClickedAt: "Unterschrieben / aktiviert",
    lastEmailStatusSent: "Gesendet", lastEmailStatusOpened: "Geöffnet",
    lastEmailStatusClicked: "Kunde aktiv", lastEmailStatusFailed: "Versand fehlgeschlagen",
    lastEmailMockedShort: "gesch.",
    lastEmailMockedHint: "Schätzung basierend auf Wallet-Aktivität — echtes Tracking kommt mit AWS SES-Setup",
    lastEmailMockedFooter: "Öffnungs- und Klick-Tracking wird aktiviert, sobald AWS SES Event Publishing auf dem LoJack-Konto konfiguriert ist.",
    lastEmailHistoryTitle: "Neuester Verlauf",
    lastEmailHistoryLoading: "Verlauf wird geladen…",
    lastEmailHistoryEmpty: "Keine vorherigen E-Mails",
    lastEmailHistoryError: "Verlauf konnte nicht geladen werden",
    timeJustNow: "gerade eben", timeMinutesAgo: "vor {n} Min",
    timeHoursAgo: "vor {n} Std", timeDaysAgo: "vor {n} Tagen",
    emailType_welcome: "Willkommen", emailType_sollecito: "Erinnerung",
    emailType_contract_signed: "Unterzeichneter Vertrag", emailType_otp_signature: "OTP-Unterschrift",
    emailType_self_update_otp: "Selbst-Update-OTP",
    emailType_self_update_confirmation: "Selbst-Update-Bestätigung",
    emailType_customer_change_request_resolution: "Ergebnis Änderungsanfrage",
    emailType_wallet_correction_resend: "Wallet erneut gesendet",
    emailType_gdpr_erasure: "DSGVO-Löschbestätigung", emailType_test_send: "Test-E-Mail",
  },
};

// Other languages fall back to EN (English) as a safer default than IT
// for a French dealer who chose ES/PT/etc. — they probably understand
// English better than Italian.
//
// Zorzutti CR 05/08/2026 — extended the fallback list to cover the
// full 25 EU-country set (previously 11 languages defaulted silently
// to IT via ``TRANSLATIONS[code] ? code : "IT"`` because they were
// missing from FALLBACK_LANGS entirely).
const FALLBACK_LANGS = [
  "NL", "PT", "PL", "SE", "NO", "FI", "DK", "CZ", "GR",
  "CH", "GBZ", "IRL", "IE", "BE",
  // Previously silent-fallback-to-IT (Zorzutti CR 05/08/2026):
  "HU", "RO", "BG", "HR", "SI", "SK", "EE", "LT", "LV", "MT", "CY",
  // Aliases to native packs (short codes used by dealer.country)
  "AT", "LU", "MC", "SM", "VA",
];
for (const code of FALLBACK_LANGS) {
  if (!TRANSLATIONS[code]) TRANSLATIONS[code] = TRANSLATIONS.EN;
}

/**
 * Resolve the active language for the EditCustomerModal.
 * Priority: explicit `lang` arg > context language > IT.
 * Context (bug 07/07/2026: residual `dealer_lang` from dealer-portal
 * testing leaked into the ADMIN customers page): `dealer_lang` is
 * honoured only inside the dealer portal (/dealer*); admin pages use
 * `admin_lang` (Italian UI default).
 */
export function getEditCustomerLang(lang) {
  if (lang && TRANSLATIONS[lang.toUpperCase()]) return lang.toUpperCase();
  if (typeof window === "undefined") return "IT";
  const isDealerContext = (window.location.pathname || "").startsWith("/dealer");
  const stored = isDealerContext
    ? window.localStorage.getItem("dealer_lang")
    : window.localStorage.getItem("admin_lang");
  const code = (stored || "IT").toUpperCase();
  return TRANSLATIONS[code] ? code : "IT";
}

/** Get the full translation dict for a given (or auto-detected) language. */
export function getEditCustomerT(lang) {
  return TRANSLATIONS[getEditCustomerLang(lang)] || TRANSLATIONS.IT;
}

export default TRANSLATIONS;
